import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { fetchDashboardStats, fetchPipeline } from "../../store/slices/dashboardSlice";
import { formatRelativeDate, getStatusColor, getStatusDot } from "../../utils/format";
import {
  DocumentTextIcon,
  PaperAirplaneIcon,
  EyeIcon,
  CheckCircleIcon,
  ClockIcon,
  UsersIcon,
  ArrowTrendingUpIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";

export default function Dashboard() {
  const dispatch = useDispatch();
  const { stats, recentActivity, popularCruiseLines, pipeline, status, pipelineStatus } =
    useSelector((state) => state.dashboard);
  const { agent } = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(fetchDashboardStats());
    dispatch(fetchPipeline());
  }, [dispatch]);

  const statCards = stats
    ? [
        { label: "Total Quotes", value: stats.totalQuotes, icon: DocumentTextIcon, color: "text-gray-600", bg: "bg-gray-50" },
        { label: "Sent", value: stats.sentQuotes, icon: PaperAirplaneIcon, color: "text-blue-600", bg: "bg-blue-50" },
        { label: "Viewed", value: stats.viewedQuotes, icon: EyeIcon, color: "text-yellow-600", bg: "bg-yellow-50" },
        { label: "Accepted", value: stats.acceptedQuotes, icon: CheckCircleIcon, color: "text-green-600", bg: "bg-green-50" },
        { label: "Conversion Rate", value: `${stats.conversionRate}%`, icon: ArrowTrendingUpIcon, color: "text-indigo-600", bg: "bg-indigo-50" },
        { label: "Clients", value: stats.clients, icon: UsersIcon, color: "text-purple-600", bg: "bg-purple-50" },
      ]
    : [];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {agent?.name?.split(" ")[0]}
        </h1>
        <p className="text-gray-500 mt-1">Here's your quoting overview</p>
      </div>

      {/* Stats grid */}
      {status === "loading" ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-20 mb-3" />
              <div className="h-8 bg-gray-200 rounded w-12" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {statCards.map((card) => (
            <div key={card.label} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {card.label}
                </span>
                <div className={`p-1.5 rounded-lg ${card.bg}`}>
                  <card.icon className={`h-4 w-4 ${card.color}`} />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{card.value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Pipeline */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Quote Pipeline</h2>
              <Link to="/quotes/new" className="text-sm text-indigo-600 font-medium hover:text-indigo-500">
                + New Quote
              </Link>
            </div>

            {pipelineStatus === "loading" ? (
              <div className="animate-pulse space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-16 bg-gray-100 rounded-lg" />
                ))}
              </div>
            ) : pipeline ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {["draft", "sent", "viewed", "accepted"].map((status) => (
                  <div key={status} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`h-2.5 w-2.5 rounded-full ${getStatusDot(status)}`} />
                      <span className="text-sm font-medium text-gray-700 capitalize">{status}</span>
                      <span className="ml-auto text-xs font-medium text-gray-500">
                        {pipeline[status]?.count || 0}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {(pipeline[status]?.quotes || []).slice(0, 3).map((q) => (
                        <Link
                          key={q._id}
                          to={`/quotes/${q._id}`}
                          className="block p-2 bg-gray-50 rounded text-xs hover:bg-gray-100 transition-colors"
                        >
                          <p className="font-medium text-gray-900 truncate">
                            {q.client.firstName} {q.client.lastName}
                          </p>
                          <p className="text-gray-500 truncate">{q.sailing.ship}</p>
                        </Link>
                      ))}
                      {(pipeline[status]?.count || 0) === 0 && (
                        <p className="text-xs text-gray-400 text-center py-2">No quotes</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        {/* Recent Activity */}
        <div>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
            <div className="space-y-3">
              {recentActivity.length === 0 && status !== "loading" && (
                <p className="text-sm text-gray-400 text-center py-4">No activity yet</p>
              )}
              {recentActivity.map((item) => (
                <Link
                  key={item._id}
                  to={`/quotes/${item._id}`}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className={`h-2 w-2 rounded-full flex-shrink-0 ${getStatusDot(item.status)}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {item.client.firstName} {item.client.lastName}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {item.sailing.ship} &bull;{" "}
                      <span className={`inline-block px-1.5 py-0.5 rounded text-xs ${getStatusColor(item.status)}`}>
                        {item.status}
                      </span>
                    </p>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">
                    {formatRelativeDate(item.updatedAt)}
                  </span>
                </Link>
              ))}
            </div>
          </div>

          {/* Popular Cruise Lines */}
          {popularCruiseLines.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Cruise Lines</h2>
              <div className="space-y-3">
                {popularCruiseLines.map((cl, idx) => (
                  <div key={cl._id} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{cl._id}</span>
                    <span className="text-sm font-medium text-gray-900">{cl.count} quotes</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
